<template>
  <div v-on-clickaway="close" class="filter">
    <EIconTextButton
      ref="button"
      :icon="icon"
      icon-size="s"
      icon-position="end"
      class="filter__button"
      :class="{ 'filter__button--highlighted': isOpen || selected }"
      data-test-id="filter-button"
      role="menuitem"
      aria-haspopup="true"
      @click="onClick"
    >
      <EColorIndicator
        v-if="selectedColor"
        :color="selectedColor"
        :show-plus="false"
        size="s"
        class="filter__button-color"
        :class="`filter__button-color--${selected}`"
        data-test-id="filter-button-color"
      />
      {{ text }}
    </EIconTextButton>
    <EModalDropdown
      v-accessible-menu
      :title="hint"
      :visible="isOpen"
      :size="size"
      :aria-label="hint"
      class="filter__content"
      role="menu"
      @close="close"
    >
      <header
        v-if="!selectedColor"
        class="filter__selected"
        data-test-id="filter-selected"
      >
        {{ text }}
        <EIcon
          class="filter__selected-icon"
          icon="check"
          size="s"
        />
      </header>
      <EList role="none">
        <EListItem
          v-for="({ value, label }) in filteredOptions"
          :key="value"
          :data-test-id="`filter-option-${value}`"
          :data-track-filter="`${name}:${value}`"
          data-track-id="filter-option"
          class="filter__item"
          role="menuitem"
          @click="onSelect(value)"
        >
          {{ label }}
        </EListItem>
      </EList>
      <EList role="none" class="filter__colors">
        <li
          v-for="({ value, label, color }) in colorOptions"
          :key="value"
          class="filter__color"
          :class="`filter__color--${value}`"
          :data-test-id="`filter-option-color-${value}`"
          :data-track-filter="`${name}:${value}`"
          data-track-id="filter-option"
          @keypress.enter.space.prevent="onSelect(value)"
          @click="onSelect(value)"
        >
          <EColorIndicator
            :color="color"
            :active="value === selected"
            role="menuitem"
            class="filter__color-indicator"
            :aria-label="label"
            :title="label"
            :dark-icons="value === 'white'"
            :size="colorIndicatorSize"
          />
        </li>
      </EList>
    </EModalDropdown>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import screen from '~~/common/vue/mixins/screen';
import EColorIndicator from '~~/common/vue/components/color-indicator';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';

import EIconTextButton from '~~/common/vue/components/icon-text-button';
import EModalDropdown from '~~/common/vue/components/modal-dropdown';

export const types = ['default', 'color'];
/**
 * A filter control with a modal-dropdown
 */
export default {
  name: 'EFilter',
  components: {
    EColorIndicator,
    EIcon,
    EIconTextButton,
    EList,
    EListItem,
    EModalDropdown,
  },
  mixins: [clickaway, screen('debounced', 100)],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    size: EModalDropdown.props.size,
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'default',
      validator: (value) => types.includes(value),
    },
    hint: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    selected: {
      type: [String, null],
      default: null,
    },
  },
  data: () => ({
    isOpen: false,
  }),
  computed: {
    isTypeColor() {
      return this.type === 'color';
    },
    filteredOptions() {
      let { options } = this;
      if (this.isTypeColor) {
        options = options.filter((option) => !this.isColorOption(option));
      }
      if (this.selected) {
        options = options.concat([{ value: '', label: this.placeholder }]);
        options = options.filter(({ value }) => value !== this.selected);
      }
      return options;
    },
    colorOptions() {
      return this.isTypeColor ? this.options.filter(this.isColorOption.bind(this)) : null;
    },
    icon() {
      return this.isOpen ? 'chevron-mini-up' : 'chevron-mini-down';
    },
    selectedText() {
      if (!this.selected) {
        return '';
      }
      const option = this.options.find(({ value }) => value === this.selected);
      return option ? option.label : '';
    },
    selectedColor() {
      if (!(this.isTypeColor && this.selected)) {
        return null;
      }
      const color = this.colorOptions.find(({ value }) => value === this.selected);
      return color ? color.color : null;
    },
    text() {
      return this.selectedText || this.placeholder;
    },
    colorIndicatorSize() {
      return this.isDesktopUp ? 's' : 'm';
    },
  },
  methods: {
    isColorOption({ color }) {
      return !!color;
    },
    onClick() {
      this.isOpen = !this.isOpen;
    },
    onSelect(option) {
      this.$emit('select', option);
      this.isOpen = false;
    },
    close() {
      if (this.isOpen) {
        this.$refs.button.$el.focus();
      }
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.filter {
  position: relative;
}

.filter__selected {
  display: flex;
  font-size: $font-m;
  font-weight: $font-weight-1l;
  justify-content: space-between;
  margin-right: $space-1s;
  padding: $space-s $space-s $space-s $space-m;
  position: relative;
  white-space: nowrap;

  @include desktop {
    margin-right: 0;
  }
}

.filter__selected-icon {
  margin-left: $space-1s;
}

.filter__item,
.filter__selected {
  box-sizing: content-box;
  padding-left: $space-m;

  @include desktop {
    font-size: $font-s;
    padding-left: $space-s;
  }
}

.filter__item {
  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.filter__button {
  @include elevation(1);

  border: none;
  color: $color-text-secondary;
  padding: 0 $space-m;

  &:active,
  &:focus,
  &:hover {
    background: $color-background-primary;
    color: $color-text-primary;
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }

  @include desktop {
    @include elevation(0);

    font-size: $font-s;
    padding: 0 $space-1s 0 0;

    &:hover {
      background: initial;
      color: $color-text-primary;
    }
  }
}

.filter__button--highlighted {
  color: $color-text-primary;
}

.filter__button-color {
  margin-right: $space-1s;
}

.filter__content {
  @include desktop {
    left: -$space-s;
  }
}

.filter__colors {
  align-self: center;
  max-width: 234px;
  margin-top: $space-1s;

  @include desktop {
    max-width: 110px;
  }
}

.filter__color {
  display: inline-block;
  cursor: pointer;
  margin-bottom: $space-m;
  margin-right: $space-l;
  transition: box-shadow $timing-2s $easing;

  &:nth-child(6n) {
    margin-right: 0;
  }

  @include desktop {
    margin-bottom: $space-s;

    &,
    &:nth-child(6n) {
      // override 0 value set above for smaller breakpoints
      margin-right: $space-m;
    }

    &:nth-child(4n) {
      margin-right: 0;
    }
  }
}

.filter__button-color--white,
.filter__color--white .filter__color-indicator {
  box-shadow: 0 0 1px $black;
}
</style>
